import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, MapPin, Maximize2, BedDouble, Pencil, Trash2, Building2, LayoutGrid, List } from "lucide-react";
import { fmtEUR, statusLabel } from "@/lib/format";
import { toast } from "sonner";

const PROP_IMAGES = [
  "https://images.unsplash.com/photo-1662454419736-de132ff75638?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Nzg0NDgyOTB8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Nzg0NDgyOTB8MA&ixlib=rb-4.1.0&q=85",
];

const empty = {
  address: "", city: "", postal_code: "", type: "appartement", surface: 0, rooms: 0,
  floor: "", lot_number: "", cadastral_ref: "", photos: [], rent: 0, charges: 0,
  deposit: 0, acquisition_date: "", acquisition_price: 0, status: "vacant",
};

export default function Biens() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("grid");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");

  const load = () => api.get("/properties").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (p) => { setEditing(p); setForm({...empty, ...p}); setOpen(true); };

  const save = async () => {
    const body = {...form, surface: +form.surface, rooms: +form.rooms, rent: +form.rent, charges: +form.charges, deposit: +form.deposit, acquisition_price: +form.acquisition_price};
    if (!body.acquisition_date) delete body.acquisition_date;
    try {
      if (editing) {
        await api.put(`/properties/${editing.id}`, {...body, id: editing.id});
        toast.success("Bien mis à jour");
      } else {
        await api.post("/properties", body);
        toast.success("Bien créé");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const del = async (id) => {
    if (!confirm("Supprimer ce bien ?")) return;
    await api.delete(`/properties/${id}`); toast.success("Supprimé"); load();
  };

  const filtered = items.filter(p =>
    !search || p.address.toLowerCase().includes(search.toLowerCase()) || p.city.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="biens-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-overline">Patrimoine</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:'Outfit'}}>Biens immobiliers</h1>
          <p className="text-sm text-zinc-500 mt-1">{items.length} bien(s) au total</p>
        </div>
        <div className="flex gap-2">
          <div className="border border-zinc-200 rounded-md flex">
            <button onClick={() => setView("grid")} data-testid="view-grid" className={`p-2 ${view === "grid" ? "bg-zinc-900 text-white" : "text-zinc-600"} rounded-l-md`}><LayoutGrid className="w-4 h-4" /></button>
            <button onClick={() => setView("list")} data-testid="view-list" className={`p-2 ${view === "list" ? "bg-zinc-900 text-white" : "text-zinc-600"} rounded-r-md`}><List className="w-4 h-4" /></button>
          </div>
          <Button data-testid="new-bien-btn" onClick={startCreate}><Plus className="w-4 h-4 mr-1.5" />Nouveau bien</Button>
        </div>
      </div>

      <Input data-testid="bien-search" placeholder="Rechercher par adresse, ville…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />

      {view === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((p, i) => (
            <Card key={p.id} className="card-flat overflow-hidden" data-testid={`bien-card-${p.id}`}>
              <div className="aspect-video bg-zinc-100 relative overflow-hidden">
                <img src={p.photos?.[0] || PROP_IMAGES[i % PROP_IMAGES.length]} alt={p.address} className="w-full h-full object-cover" />
                <Badge className={`absolute top-3 right-3 status-${p.status}`}>{statusLabel[p.status]}</Badge>
              </div>
              <div className="p-5">
                <div className="text-xs text-zinc-500 uppercase tracking-wider">{statusLabel[p.type]}</div>
                <div className="font-semibold mt-1 line-clamp-1">{p.address}</div>
                <div className="text-sm text-zinc-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {p.postal_code} {p.city}</div>
                <div className="flex items-center gap-3 text-xs text-zinc-600 mt-3">
                  <span className="flex items-center gap-1"><Maximize2 className="w-3 h-3" /> {p.surface}m²</span>
                  <span className="flex items-center gap-1"><BedDouble className="w-3 h-3" /> {p.rooms} pièces</span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-100">
                  <div>
                    <div className="text-overline">Loyer + charges</div>
                    <div className="font-bold mono">{fmtEUR(p.rent + p.charges)}<span className="text-xs text-zinc-500 font-normal">/mois</span></div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(p)} data-testid={`edit-bien-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(p.id)} data-testid={`del-bien-${p.id}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
          {filtered.length === 0 && <Card className="card-flat p-10 text-center text-zinc-500 col-span-full"><Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />Aucun bien. Cliquez sur "Nouveau bien" pour commencer.</Card>}
        </div>
      ) : (
        <Card className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3">Adresse</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Surface</th>
                <th className="px-4 py-3">Loyer</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3"><div className="font-medium">{p.address}</div><div className="text-xs text-zinc-500">{p.postal_code} {p.city}</div></td>
                  <td className="px-4 py-3">{statusLabel[p.type]}</td>
                  <td className="px-4 py-3 mono">{p.surface}m² · {p.rooms}p</td>
                  <td className="px-4 py-3 mono font-medium">{fmtEUR(p.rent + p.charges)}</td>
                  <td className="px-4 py-3"><Badge className={`status-${p.status}`}>{statusLabel[p.status]}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(p)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(p.id)}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bien-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifier le bien" : "Nouveau bien"}</DialogTitle></DialogHeader>
          <Tabs defaultValue="general">
            <TabsList className="grid grid-cols-3"><TabsTrigger value="general">Général</TabsTrigger><TabsTrigger value="financial">Financier</TabsTrigger><TabsTrigger value="legal">Cadastre</TabsTrigger></TabsList>
            <TabsContent value="general" className="space-y-3 pt-3">
              <div><Label>Adresse *</Label><Input data-testid="bien-address" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Code postal</Label><Input value={form.postal_code} onChange={(e) => setForm({...form, postal_code: e.target.value})} /></div>
                <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                    <SelectTrigger data-testid="bien-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appartement">Appartement</SelectItem><SelectItem value="maison">Maison</SelectItem>
                      <SelectItem value="studio">Studio</SelectItem><SelectItem value="local_commercial">Local commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vacant">Vacant</SelectItem><SelectItem value="occupe">Occupé</SelectItem><SelectItem value="travaux">En travaux</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Surface (m²)</Label><Input type="number" value={form.surface} onChange={(e) => setForm({...form, surface: e.target.value})} /></div>
                <div><Label>Pièces</Label><Input type="number" value={form.rooms} onChange={(e) => setForm({...form, rooms: e.target.value})} /></div>
                <div><Label>Étage</Label><Input value={form.floor} onChange={(e) => setForm({...form, floor: e.target.value})} /></div>
              </div>
              <div><Label>Photo (URL)</Label><Input value={form.photos?.[0] || ""} onChange={(e) => setForm({...form, photos: e.target.value ? [e.target.value] : []})} placeholder="https://…" /></div>
            </TabsContent>
            <TabsContent value="financial" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Loyer HC (€)</Label><Input data-testid="bien-rent" type="number" value={form.rent} onChange={(e) => setForm({...form, rent: e.target.value})} /></div>
                <div><Label>Charges (€)</Label><Input type="number" value={form.charges} onChange={(e) => setForm({...form, charges: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Dépôt de garantie (€)</Label><Input type="number" value={form.deposit} onChange={(e) => setForm({...form, deposit: e.target.value})} /></div>
                <div><Label>Prix d'acquisition (€)</Label><Input type="number" value={form.acquisition_price} onChange={(e) => setForm({...form, acquisition_price: e.target.value})} /></div>
              </div>
              <div><Label>Date d'acquisition</Label><Input type="date" value={form.acquisition_date || ""} onChange={(e) => setForm({...form, acquisition_date: e.target.value})} /></div>
            </TabsContent>
            <TabsContent value="legal" className="space-y-3 pt-3">
              <div><Label>Numéro de lot</Label><Input value={form.lot_number} onChange={(e) => setForm({...form, lot_number: e.target.value})} /></div>
              <div><Label>Référence cadastrale</Label><Input value={form.cadastral_ref} onChange={(e) => setForm({...form, cadastral_ref: e.target.value})} /></div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="bien-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
